import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setModel,
  queueRequest,
  update,
  setDefaults,
  reset,
  resetParameters,
  validateParams,
  glimpseParametersSchema,
  getFieldRules,
} from "./index";
import { computeFrameSizeList } from "../../components/glimpse/frameSizeListUtil";

export const useGlimpse = () => {
  const dispatch = useDispatch();
  const glimpse = useSelector((state) => state.glimpse);

  // Set model clip and calculate defaults
  const handleSetModel = useCallback(
    (modelCaseId, modelClipId) => {
      return dispatch(setModel({ modelCaseId, modelClipId }));
    },
    [dispatch],
  );

  // Queue a glimpse processing request
  const handleQueueRequest = useCallback(
    (modelCaseId, modelClipId, sourceCaseId, sourceClipId, parameters) => {
      return dispatch(
        queueRequest({
          modelCaseId,
          modelClipId,
          sourceCaseId,
          sourceClipId,
          parameters,
        }),
      );
    },
    [dispatch],
  );

  // Update glimpse parameters
  const handleUpdate = useCallback(
    (updates) => {
      return dispatch(update(updates));
    },
    [dispatch],
  );

  // Set default parameters
  const handleSetDefaults = useCallback(
    (defaults) => {
      return dispatch(setDefaults(defaults));
    },
    [dispatch],
  );

  // Reset glimpse state
  const handleReset = useCallback(() => {
    return dispatch(reset());
  }, [dispatch]);

  // Reset glimpse parameters
  const handleResetParameters = useCallback(() => {
    return dispatch(resetParameters());
  }, [dispatch]);

  // Validate current parameters
  const handleValidate = useCallback(() => {
    return validateParams(glimpse);
  }, [glimpse]);

  // Get validation errors for a specific field
  const getFieldErrors = useCallback(
    (fieldName) => {
      return glimpse.validationErrors?.fieldErrors[fieldName] || [];
    },
    [glimpse.validationErrors],
  );

  // Get all cross-field validation errors
  const getCrossFieldErrors = useCallback(() => {
    return glimpse.validationErrors?.crossFieldErrors || [];
  }, [glimpse.validationErrors]);

  const getValidationRules = useCallback((fieldName) => {
    return getFieldRules(fieldName);
  }, []);

  // Parse and validate data with Zod schema
  const parseWithZod = useCallback((data) => {
    try {
      return glimpseParametersSchema.parse(data);
    } catch (error) {
      console.error("Zod validation error:", error);
      throw error;
    }
  }, []);

  const safeParseWithZod = useCallback((data) => {
    return glimpseParametersSchema.safeParse(data);
  }, []);

  // Check if a specific field is valid
  const isFieldValid = useCallback(
    (fieldName) => {
      const errors = getFieldErrors(fieldName);
      return errors.length === 0;
    },
    [getFieldErrors],
  );

  // Get all processing parameters (excluding metadata and loading states)
  const getProcessingParameters = useCallback(() => {
    const {
      modelClipSnapshot,
      modelCaseId,
      modelClipId,
      modelAudioDuration,
      valid,
      settingModel,
      queueing,
      useFrameSizeList,
      frameSizeListMin,
      frameSizeListMax,
      frameSizeListSteps,
      frameSizeLow,
      frameSizeHi,
      ...processingParams
    } = glimpse;

    return {
      ...processingParams,
      ...(useFrameSizeList
        ? {
            frameSizeList: computeFrameSizeList(
              frameSizeListMin,
              frameSizeListMax,
              frameSizeListSteps,
            ),
          }
        : { frameSizeLow, frameSizeHi }),
    };
  }, [glimpse]);

  return {
    // State - Model information
    modelClipSnapshot: glimpse.modelClipSnapshot,
    modelCaseId: glimpse.modelCaseId,
    modelClipId: glimpse.modelClipId,
    modelAudioDuration: glimpse.modelAudioDuration,

    // State - Processing parameters
    adaptMethod: glimpse.adaptMethod,
    frameSizeLow: glimpse.frameSizeLow,
    frameSizeHi: glimpse.frameSizeHi,
    useFrameSizeList: glimpse.useFrameSizeList,
    frameSizeListMin: glimpse.frameSizeListMin,
    frameSizeListMax: glimpse.frameSizeListMax,
    frameSizeListSteps: glimpse.frameSizeListSteps,
    speedOfSoundInCm: glimpse.speedOfSoundInCm,
    gateLevelGF: glimpse.gateLevelGF,
    midModelLengthGF: glimpse.midModelLengthGF,
    utteranceLength: glimpse.utteranceLength,
    gateLevelD: glimpse.gateLevelD,
    midModelLengthD: glimpse.midModelLengthD,
    lowPass: glimpse.lowPass,
    highPass: glimpse.highPass,
    arrayWidth: glimpse.arrayWidth,

    // State - Validation and loading
    isValid: glimpse.valid,
    validationErrors: glimpse.validationErrors,
    isSettingModel: glimpse.settingModel,
    isQueueing: glimpse.queueing,

    // Derived state
    isBusy: glimpse.settingModel || glimpse.queueing,
    hasModel: glimpse.modelClipSnapshot !== undefined,
    canQueue: glimpse.valid && glimpse.modelClipSnapshot !== undefined,

    // Actions
    setModel: handleSetModel,
    queueRequest: handleQueueRequest,
    update: handleUpdate,
    setDefaults: handleSetDefaults,
    reset: handleReset,
    resetParameters: handleResetParameters,
    validate: handleValidate,
    getProcessingParameters,

    // Validation helpers
    getFieldErrors,
    getCrossFieldErrors,
    getFieldRules,
    getValidationRules,
    isFieldValid,
    parseWithZod,
    safeParseWithZod,
  };
};
